import FcOpenItemsTable from '../common/FcOpenItemsTable';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { FileDownload as FileDownloadIcon, Print as PrintIcon } from '@mui/icons-material';
import { SalesReportService } from '../../services/salesReport.service';
import { CustomerService } from '../../services/customer.service';
import { CustomerStatement as CustomerStatementData } from '../../interfaces/sales/salesReport.interface';
import { Customer } from '../../interfaces/sales/customer.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { downloadCsv } from '../../utils/csv';
import { StatementDeliveryService } from '../../services/collections.service';
import { saveBlob } from '../../utils/saveBlob';
import SearchableSelect from '../common/SearchableSelect';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatFc } from '../../utils/fcDocuments';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';

const toYmd = (date: Date): string => toLocalIsoDate(date);

const CustomerStatement: React.FC = () => {
  const toast = useToast();
  const [searchParams] = useSearchParams();

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(true);

  const [customerId, setCustomerId] = useState<string>(searchParams.get('customerId') ?? '');
  const [from, setFrom] = useState<string>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 12);
    return toYmd(d);
  });
  const [to, setTo] = useState<string>(() => toYmd(new Date()));

  const [statement, setStatement] = useState<CustomerStatementData | null>(null);
  const anyFc = !!statement?.rows.some(r => !!r.currencyCode);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const institutionId = AuthService.getSelectedInstitution();
    CustomerService.listByInstitution(institutionId, 0, 200, 'ALL')
      .then(page => setCustomers(page.content))
      .catch(e => toast.showApiError(e, 'Failed to load customers'))
      .finally(() => setCustomersLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Opened from a customer ("View Statement" / aging row): run straight away with the default range.
  useEffect(() => {
    if (customerId) handleRun();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRun = async () => {
    if (!customerId) return;
    setStatement(null);
    try {
      setLoading(true);
      const data = await SalesReportService.statement(customerId, from, to);
      setStatement(data);
    } catch (e) {
      toast.showApiError(e, 'Failed to load customer statement');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = () => {
    if (!statement) return;
    const headers = ['Date', 'Entry #', 'Type', 'Reference', 'Debit', 'Credit', ...(anyFc ? ['Foreign amount'] : []), 'Running Balance'];
    const dataRows = statement.rows.map(r => [
      formatDate(r.date),
      r.entryNumber,
      humanizeEnum(r.documentType),
      r.reference,
      r.debit > 0 ? r.debit.toFixed(2) : '',
      r.credit > 0 ? r.credit.toFixed(2) : '',
      ...(anyFc ? [r.currencyCode && r.foreignAmount != null ? formatFc(r.currencyCode, r.foreignAmount) : ''] : []),
      r.runningBalance.toFixed(2),
    ]);
    const gap = anyFc ? [''] : [];
    const openingRow = ['', '', 'Opening Balance', '', '', '', ...gap, statement.openingBalance.toFixed(2)];
    const closingRow = ['', '', 'Closing Balance', '', '', '', ...gap, statement.closingBalance.toFixed(2)];
    const safeName = statement.customerName.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'customer';
    downloadCsv(`statement-${safeName}-${toYmd(new Date())}.csv`, headers, [openingRow, ...dataRows, closingRow]);
  };

  // ---- W3-N: statement as PDF and by e-mail ----
  const [busyDelivery, setBusyDelivery] = useState(false);
  const handlePdf = async () => {
    if (busyDelivery) return;
    setBusyDelivery(true);
    try {
      const blob = await StatementDeliveryService.pdf(customerId, from, to);
      saveBlob(blob, `statement-${to}.pdf`);
    } catch (e) {
      toast.showApiError(e, 'Failed to build the statement PDF');
    } finally {
      setBusyDelivery(false);
    }
  };
  const handleEmail = async () => {
    if (busyDelivery) return;
    if (!window.confirm('E-mail this statement to the customer?')) return;
    setBusyDelivery(true);
    try {
      const r = await StatementDeliveryService.email(customerId, from, to);
      toast.showSuccess(`Statement e-mailed to ${r.email ?? 'the customer'}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to e-mail the statement');
    } finally {
      setBusyDelivery(false);
    }
  };
  // ---- end W3-N ----

  const handlePrint = () => {
    window.print();
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.statement.page">
      {/* Page header */}
      <Typography variant="h5" gutterBottom>Customer Statement</Typography>

      {/* Filter row */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 3 }} data-help-section="sales.statement.filters">
        <SearchableSelect
          data-help="sales.statement.customer"
          label="Customer"
          size="small"
          sx={{ flex: '1 1 260px', maxWidth: 360 }}
          options={customers}
          value={customerId}
          onChange={id => {
            setCustomerId(id);
            setStatement(null);
          }}
          getOptionLabel={c => c.name}
          disabled={customersLoading}
        />

        <TextField
          data-help="sales.statement.from"
          label="From"
          type="date"
          size="small"
          value={from}
          onChange={e => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          data-help="sales.statement.to"
          label="To"
          type="date"
          size="small"
          value={to}
          onChange={e => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <Button
          variant="contained"
          data-help="sales.statement.run"
          onClick={handleRun}
          disabled={!customerId || loading}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Run'}
        </Button>
      </Box>

      {/* Statement area */}
      {statement && (
        <Box>
          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              data-help="sales.statement.exportCsv"
              onClick={handleExportCsv}
            >
              Export CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              data-help="sales.statement.print"
              onClick={handlePrint}
            >
              Print
            </Button>
            <Button variant="outlined" disabled={busyDelivery} data-help="sales.statement.pdf" onClick={handlePdf}>PDF</Button>
            <Button variant="outlined" disabled={busyDelivery} data-help="sales.statement.email" onClick={handleEmail}>E-mail statement</Button>
          </Box>

          {/* Printable area */}
          <Paper sx={{ p: 3 }} className="printable-area">
            {/* Statement header */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">{statement.customerName}</Typography>
              <Typography variant="body2" color="text.secondary">
                Period: {formatDate(statement.fromDate)} – {formatDate(statement.toDate)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 4, mt: 1 }}>
                <Typography variant="body2">
                  <strong>Opening Balance:</strong> {formatMoney(statement.openingBalance)}
                </Typography>
                <Typography variant="body2">
                  <strong>Closing Balance:</strong> {formatMoney(statement.closingBalance)}
                </Typography>
              </Box>
            </Box>

            {/* Statement table (B4-polish3: foreign-amount column only when a row belongs to a foreign-currency invoice) */}
            <TableContainer data-help-section="sales.statement.table">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="sales.statement.col.date">Date</TableCell>
                    <TableCell data-help="sales.statement.col.entry">Entry #</TableCell>
                    <TableCell data-help="sales.statement.col.type">Type</TableCell>
                    <TableCell data-help="sales.statement.col.reference">Reference</TableCell>
                    <TableCell align="right" data-help="sales.statement.col.debit">Debit</TableCell>
                    <TableCell align="right" data-help="sales.statement.col.credit">Credit</TableCell>
                    <TableCell align="right" data-help="sales.statement.col.balance">Running Balance</TableCell>
                    {anyFc && <TableCell align="right" data-help="sales.statement.col.foreign">Foreign amount</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statement.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={anyFc ? 8 : 7} align="center">No transactions in this period.</TableCell>
                    </TableRow>
                  ) : (
                    statement.rows.map((row, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{formatDate(row.date)}</TableCell>
                        <TableCell>{row.entryNumber}</TableCell>
                        <TableCell>{humanizeEnum(row.documentType)}</TableCell>
                        <TableCell>{row.reference}</TableCell>
                        <TableCell align="right">{row.debit > 0 ? formatMoney(row.debit) : ''}</TableCell>
                        <TableCell align="right">{row.credit > 0 ? formatMoney(row.credit) : ''}</TableCell>
                        <TableCell align="right">{formatMoney(row.runningBalance)}</TableCell>
                        {anyFc && (
                          <TableCell align="right">
                            {row.currencyCode && row.foreignAmount != null ? formatFc(row.currencyCode, row.foreignAmount) : ''}
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <FcOpenItemsTable items={statement.foreignOpenItems} />
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default CustomerStatement;
